import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Phone, MessageSquare, Shield, X, Mic, MicOff, Send, Loader2, User, Bot, Volume2, Globe, Zap } from 'lucide-react';
import { useAuth } from './AuthProvider';
import { db, collection, addDoc, query, where, orderBy, limit, onSnapshot, handleFirestoreError, OperationType } from '../firebase';

export const SupportPortal: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'phone' | 'text'>('phone');
  const [isCalling, setIsCalling] = useState(false);
  const [callTime, setCallTime] = useState(0);
  const [isMicMuted, setIsMicMuted] = useState(false);
  const [chatMessages, setChatMessages] = useState<any[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<any>(null);

  // Persistence Logic for Call
  useEffect(() => {
    if (isCalling) {
      // Request Wake Lock to prevent phone from sleeping
      if ('wakeLock' in navigator) {
        (navigator as any).wakeLock.request('screen').catch(() => {});
      }
      
      timerRef.current = setInterval(() => {
        setCallTime(prev => prev + 1);
      }, 1000);

      // Heartbeat to server to signify active session
      const heartbeat = setInterval(() => {
        const deviceId = localStorage.getItem('lattice_device_id') || 'anonymous';
        fetch('/api/devices/offer', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            deviceId,
            name: `Active Support Session (${user?.displayName || 'Guest'})`,
            type: 'browser',
            heartbeat: true
          })
        }).catch(() => {});
      }, 10000);

      return () => {
        clearInterval(timerRef.current);
        clearInterval(heartbeat);
      };
    } else {
      clearInterval(timerRef.current);
      setCallTime(0);
    }
  }, [isCalling, user]);

  // Firestore Chat Logic
  useEffect(() => {
    if (activeTab === 'text' && user) {
      const q = query(
        collection(db, 'support_chat'),
        where('uid', '==', user.uid),
        orderBy('timestamp', 'asc'),
        limit(50)
      );

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const msgs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setChatMessages(msgs);
      }, (err) => handleFirestoreError(err, OperationType.LIST, 'support_chat'));

      return () => unsubscribe();
    }
  }, [activeTab, user]);

  const startCall = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      setIsCalling(true);
      
      // Simulate connection to Lattice AI
      setTimeout(() => {
        const audio = new Audio('https://www.soundjay.com/buttons/button-10.mp3');
        audio.play().catch(() => {});
      }, 500);
    } catch (err) {
      console.error('Call failed:', err);
      alert('Microphone access is required for sovereign support calls.');
    }
  };

  const endCall = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    setIsCalling(false);
  };

  const sendChatMessage = async () => {
    if (!chatInput.trim() || !user) return;
    const content = chatInput.trim();
    setChatInput('');
    setIsTyping(true);

    try {
      await addDoc(collection(db, 'support_chat'), {
        uid: user.uid,
        author: user.displayName || 'Sovereign Node',
        content,
        timestamp: new Date().toISOString(),
        role: 'user'
      });

      // Simulated AI response
      setTimeout(async () => {
        await addDoc(collection(db, 'support_chat'), {
          uid: user.uid, // Keep the same UID for the conversation thread visibility
          author: 'Lattice Architect',
          content: 'Your signal is received. We are optimizing our persistence layers for your node. Keep the app grounding active for indefinite stability.',
          timestamp: new Date().toISOString(),
          role: 'ai'
        });
        setIsTyping(false);
      }, 1500);
    } catch (err) {
      handleFirestoreError(err, OperationType.WRITE, 'support_chat');
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[200] flex items-end md:items-center justify-center pointer-events-none">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/60 backdrop-blur-md pointer-events-auto"
          />
          
          <motion.div 
            initial={{ y: 100, opacity: 0, scale: 0.95 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 100, opacity: 0, scale: 0.95 }}
            className="w-full max-w-2xl bg-[#050505] border-t md:border border-white/10 rounded-t-3xl md:rounded-3xl overflow-hidden shadow-[0_-20px_50px_rgba(0,0,0,0.5)] pointer-events-auto relative z-10"
          >
            {/* Header */}
            <div className="p-6 border-b border-white/5 flex items-center justify-between bg-white/[0.02]">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-lattice-cyan/10 text-lattice-cyan animate-pulse">
                  <Shield className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-xl font-display font-bold">SOVEREIGN SUPPORT</h3>
                  <p className="text-[10px] font-mono text-white/40 uppercase tracking-widest">Grounding Persistence Enabled</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex bg-black/40 p-1 rounded-lg border border-white/5">
                  <button 
                    onClick={() => setActiveTab('phone')}
                    className={`px-4 py-1.5 rounded-md text-[10px] font-mono transition-all ${activeTab === 'phone' ? 'bg-lattice-cyan text-black' : 'text-white/40 hover:text-white'}`}
                  >
                    PHONE
                  </button>
                  <button 
                    onClick={() => setActiveTab('text')}
                    className={`px-4 py-1.5 rounded-md text-[10px] font-mono transition-all ${activeTab === 'text' ? 'bg-lattice-cyan text-black' : 'text-white/40 hover:text-white'}`}
                  >
                    TEXT
                  </button>
                </div>
                <button onClick={onClose} className="p-2 text-white/20 hover:text-white transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content Area */}
            <div className="h-[400px] md:h-[500px] overflow-hidden flex flex-col">
              {activeTab === 'phone' ? (
                <div className="flex-1 flex flex-col items-center justify-center p-8 text-center relative">
                  <div className="absolute top-4 left-4 flex flex-col items-start gap-1">
                    <div className="flex items-center gap-1.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                      <span className="text-[8px] font-mono text-green-500 uppercase">Lattice Signal Encrypted</span>
                    </div>
                  </div>

                  <AnimatePresence mode="wait">
                    {!isCalling ? (
                      <motion.div 
                        key="idle"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        className="flex flex-col items-center"
                      >
                        <div className="w-32 h-32 rounded-full border-2 border-white/10 flex items-center justify-center mb-6 group cursor-pointer hover:border-lattice-cyan/50 transition-colors" onClick={startCall}>
                          <Phone className="w-12 h-12 text-white/20 group-hover:text-lattice-cyan transition-colors" />
                        </div>
                        <h4 className="text-2xl font-display font-bold mb-2">Initialize Sovereign Link</h4>
                        <p className="text-sm text-white/40 max-w-sm">
                          Direct audio transmission with the Architect Node. Zero background noise, 100% derivation accuracy.
                        </p>
                        <button 
                          onClick={startCall}
                          className="mt-8 px-8 py-3 bg-lattice-cyan text-black font-display font-bold rounded-lg hover:scale-105 transition-transform"
                        >
                          START CALL
                        </button>
                      </motion.div>
                    ) : (
                      <motion.div 
                        key="calling"
                        initial={{ opacity: 0, scale: 1.1 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 1.1 }}
                        className="flex flex-col items-center w-full"
                      >
                        <div className="relative mb-12">
                          <div className="absolute inset-0 rounded-full bg-lattice-cyan/20 animate-ping" />
                          <div className="w-32 h-32 rounded-full bg-lattice-cyan/10 border-2 border-lattice-cyan flex items-center justify-center relative z-10">
                            <Volume2 className="w-12 h-12 text-lattice-cyan animate-pulse" />
                          </div>
                        </div>
                        
                        <div className="text-center mb-12">
                          <h4 className="text-3xl font-display font-bold text-lattice-cyan mb-1 tracking-tight">Active Lattice Link</h4>
                          <div className="flex items-center justify-center gap-2 font-mono text-white/40">
                            <span className="w-2 h-2 rounded-full bg-lattice-cyan animate-pulse" />
                            <span>{formatTime(callTime)}</span>
                          </div>
                        </div>

                        <div className="flex gap-6">
                          <button 
                            onClick={() => setIsMicMuted(!isMicMuted)}
                            className={`p-5 rounded-full border transition-all ${isMicMuted ? 'bg-white/10 border-white/20 text-white' : 'bg-black/40 border-white/10 text-white/40 hover:border-white/30'}`}
                          >
                            {isMicMuted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
                          </button>
                          <button 
                            onClick={endCall}
                            className="p-5 rounded-full bg-red-500 text-white hover:scale-110 transition-transform shadow-[0_0_30px_rgba(239,68,68,0.4)]"
                          >
                            <Phone className="w-6 h-6 rotate-[135deg]" />
                          </button>
                        </div>

                        <div className="mt-12 p-4 rounded-xl bg-white/5 border border-white/10 max-w-xs transition-all animate-pulse">
                          <p className="text-[10px] font-mono text-white/60 leading-relaxed text-left">
                            <span className="text-lattice-cyan">Architect:</span> Listening for potential 1.0 drift corrections. Signal is grounded via your current node ID.
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <div className="flex-1 flex flex-col min-h-0 bg-black/20">
                  <div className="flex-1 overflow-y-auto p-6 space-y-4">
                    {chatMessages.length === 0 && !isTyping ? (
                      <div className="h-full flex flex-col items-center justify-center text-center opacity-40">
                        <MessageSquare className="w-12 h-12 mb-4" />
                        <p className="text-xs font-mono uppercase tracking-widest leading-relaxed max-w-[200px]">
                          Initialize text broadcast for live lattice support
                        </p>
                      </div>
                    ) : (
                      chatMessages.map((msg, i) => (
                        <motion.div 
                          key={msg.id || i}
                          initial={{ opacity: 0, x: msg.role === 'user' ? 20 : -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className={`flex gap-3 max-w-[85%] ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                              msg.role === 'user' ? 'bg-white/10' : 'bg-lattice-cyan/10 text-lattice-cyan'
                            }`}>
                              {msg.role === 'user' ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
                            </div>
                            <div className={`p-3 rounded-2xl text-[11px] leading-relaxed break-words font-mono ${
                              msg.role === 'user' ? 'bg-lattice-cyan text-black font-bold' : 'bg-white/5 border border-white/10 text-white/70'
                            }`}>
                              {msg.content}
                            </div>
                          </div>
                        </motion.div>
                      ))
                    )}
                    {isTyping && (
                      <div className="flex gap-3">
                        <div className="w-8 h-8 rounded-lg bg-lattice-cyan/10 text-lattice-cyan flex items-center justify-center animate-pulse">
                          <Bot className="w-4 h-4" />
                        </div>
                        <div className="p-3 bg-white/5 border border-white/10 rounded-2xl flex items-center gap-1.5">
                          <div className="w-1 h-1 bg-lattice-cyan rounded-full animate-bounce [animation-delay:-0.3s]" />
                          <div className="w-1 h-1 bg-lattice-cyan rounded-full animate-bounce [animation-delay:-0.15s]" />
                          <div className="w-1 h-1 bg-lattice-cyan rounded-full animate-bounce" />
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="p-4 border-t border-white/10 bg-black/40">
                    <div className="relative">
                      <input 
                        type="text" 
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && sendChatMessage()}
                        placeholder="Type message..."
                        className="w-full bg-white/5 border border-white/10 rounded-xl pl-4 pr-12 py-3 focus:outline-none focus:border-lattice-cyan/50 text-xs font-mono"
                      />
                      <button 
                        onClick={sendChatMessage}
                        disabled={!chatInput.trim()}
                        className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-lg bg-lattice-cyan text-black flex items-center justify-center hover:scale-105 transition-transform disabled:opacity-30"
                      >
                        <Send className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Persistence Footer */}
            <div className="p-4 bg-lattice-cyan/5 border-t border-lattice-cyan/10 flex items-center justify-between overflow-hidden relative">
              <div className="absolute inset-0 flex items-center justify-center opacity-10 pointer-events-none">
                <motion.div 
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 1.09, repeat: Infinity }}
                  className="w-32 h-32 rounded-full border border-lattice-cyan"
                />
              </div>
              <div className="flex items-center gap-3 relative z-10">
                <div className={`w-2 h-2 rounded-full ${isCalling ? 'bg-lattice-cyan animate-pulse shadow-[0_0_10px_#00ffd5]' : 'bg-white/10'}`} />
                <span className="text-[10px] font-mono text-white/40 uppercase tracking-widest">Persistence Shield: {isCalling ? 'ACTIVE' : 'IDLE'}</span>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 opacity-30 group cursor-help">
                  <Globe className="w-3 h-3" />
                  <span className="text-[8px] font-mono">Mesh Route: SECURE</span>
                </div>
                <div className="flex items-center gap-2 opacity-30">
                  <Zap className="w-3 h-3 text-lattice-cyan" />
                  <span className="text-[8px] font-mono text-lattice-cyan">1.09s Pulse</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
